import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth";
import { useEntitlement } from "@/hooks/useEntitlement";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Check, ExternalLink, ShieldCheck, CreditCard, FileText, RefreshCw, XCircle } from "lucide-react";
import { toast } from "sonner";
import { useServerFn } from "@tanstack/react-start";
import { useStripeCheckout } from "@/hooks/useStripeCheckout";
import { createPortalSession, changeSubscriptionPlan, cancelSubscription, selectFreePlan, syncProductTaxCodes, reactivateSubscription } from "@/lib/payments.functions";
import { getStripeEnvironment, isPaymentsConfigured } from "@/lib/stripe";
import { PaymentTestModeBanner } from "@/components/PaymentTestModeBanner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { trackAppEvent } from "@/lib/app-analytics";
import { stashCheckoutReturnTo } from "@/lib/checkout-return-to";

type BillingSearch = {
  plan?: string;
  interval?: "month" | "year";
  returnTo?: string;
};

export const Route = createFileRoute("/_authenticated/billing")({
  component: BillingPage,
  validateSearch: (search: Record<string, unknown>): BillingSearch => {
    const interval = search.interval === "year" || search.interval === "month" ? search.interval : undefined;
    const plan = typeof search.plan === "string" ? search.plan : undefined;
    const returnToRaw = typeof search.returnTo === "string" ? search.returnTo : undefined;
    // Only allow same-origin app paths to prevent open-redirect via the param.
    const returnTo = returnToRaw && returnToRaw.startsWith("/") && !returnToRaw.startsWith("//") ? returnToRaw : undefined;
    return { plan, interval, returnTo };
  },
  head: () => ({ meta: [{ title: "Billing & plan — MissionReady" }] }),
});

type Plan = {
  id: string;
  key: string;
  name: string;
  description: string | null;
  price_cents: number;
  currency: string;
  interval: string;
  features: string[];
  max_seats: number | null;
  sort_order: number;
  stripe_price_id: string | null;
};

type Invoice = {
  id: string;
  number: string | null;
  amount_due_cents: number;
  amount_paid_cents: number;
  currency: string;
  status: string;
  hosted_invoice_url: string | null;
  invoice_pdf: string | null;
  period_start: string | null;
  period_end: string | null;
  paid_at: string | null;
  created_at: string;
  failure_reason: string | null;
  failure_code: string | null;
  next_payment_attempt: string | null;
  attempt_count: number;
};

const FAILURE_STATUSES = new Set(["past_due", "unpaid", "uncollectible", "incomplete"]);

const friendlyFailureReason = (code: string | null, reason: string | null): string => {
  if (reason && reason.trim()) return reason;
  switch (code) {
    case "card_declined": return "Your card was declined by the issuing bank.";
    case "insufficient_funds": return "The card had insufficient funds.";
    case "expired_card": return "The card on file has expired.";
    case "incorrect_cvc": return "The card's security code (CVC) was incorrect.";
    case "processing_error": return "A temporary processing error occurred at the bank.";
    case "authentication_required": return "The bank requires additional authentication (3D Secure).";
    default: return "The most recent payment attempt was unsuccessful.";
  }
};

const fmtDateTime = (s: string | null) =>
  s ? new Date(s).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }) : "—";

const fmtMoney = (cents: number, currency = "usd") =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: currency.toUpperCase() }).format(cents / 100);

const fmtDate = (s: string | null) => (s ? new Date(s).toLocaleDateString() : "—");

function StatusBadge({ status }: { status: string }) {
  const map: Record<string, { label: string; cls: string }> = {
    active: { label: "Active", cls: "bg-success/15 text-success border-success/40" },
    trialing: { label: "Trialing", cls: "bg-primary/15 text-primary border-primary/40" },
    past_due: { label: "Past due", cls: "bg-destructive/15 text-destructive border-destructive/40" },
    canceled: { label: "Canceled", cls: "bg-muted text-muted-foreground border-border" },
    inactive: { label: "Inactive", cls: "bg-muted text-muted-foreground border-border" },
    incomplete: { label: "Incomplete", cls: "bg-amber-500/15 text-amber-400 border-amber-500/40" },
    unpaid: { label: "Unpaid", cls: "bg-destructive/15 text-destructive border-destructive/40" },
  };
  const v = map[status] ?? map.inactive;
  return <Badge variant="outline" className={v.cls}>{v.label}</Badge>;
}

function BillingPage() {
  const { currentOrg, currentRole, user } = useAuth();
  const ent = useEntitlement();
  const search = Route.useSearch();
  const navigate = useNavigate();
  const [plans, setPlans] = useState<Plan[]>([]);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [billingInterval, setBillingInterval] = useState<"month" | "year">(search.interval ?? "month");
  const [highlightedPlanKey, setHighlightedPlanKey] = useState<string | null>(search.plan ?? null);
  const autoTriggeredRef = useRef(false);

  const canManage = currentRole === "org_owner" || currentRole === "admin" || ent.exempt;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      const env = getStripeEnvironment();
      const [{ data: planData }, invRes] = await Promise.all([
        supabase.from("subscription_plans").select("*").eq("active", true).order("sort_order"),
        currentOrg
          ? supabase
              .from("invoices")
              .select("*")
              .eq("organization_id", currentOrg.id)
              .eq("environment", env)
              .order("created_at", { ascending: false })
              .limit(50)
          : Promise.resolve({ data: [] as Invoice[] }),
      ]);
      if (cancelled) return;
      setPlans(((planData ?? []) as unknown) as Plan[]);
      setInvoices(((invRes.data ?? []) as unknown) as Invoice[]);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [currentOrg?.id]);

  const { openCheckout, closeCheckout, isOpen: checkoutOpen, checkoutElement } = useStripeCheckout();
  const openPortal = useServerFn(createPortalSession);
  const changePlan = useServerFn(changeSubscriptionPlan);
  const cancelSub = useServerFn(cancelSubscription);
  const chooseFree = useServerFn(selectFreePlan);
  const runTaxSync = useServerFn(syncProductTaxCodes);
  const reactivate = useServerFn(reactivateSubscription);
  const [, setChanging] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [reactivating, setReactivating] = useState(false);
  const [syncingTax, setSyncingTax] = useState(false);

  const goToReturnTo = () => {
    if (search.returnTo) {
      navigate({ to: search.returnTo as any });
      return true;
    }
    return false;
  };

  const handleSelectPlan = async (plan: Plan, options?: { autoTriggered?: boolean }) => {
    if (!canManage) {
      toast.error("Only org owners and admins can change the plan.");
      return;
    }
    if (!currentOrg) return;

    trackAppEvent(
      "billing_plan_selected",
      {
        plan_key: plan.key,
        interval: plan.interval,
        auto_triggered: !!options?.autoTriggered,
        return_to: search.returnTo,
      },
      { userId: user?.id, orgId: currentOrg?.id },
    );

    if (plan.key === "free") {
      try {
        await chooseFree({
          data: { organizationId: currentOrg.id, environment: getStripeEnvironment() },
        });
        toast.success("You're on the Free Forever plan.");
        await ent.refresh();
        goToReturnTo();
      } catch (e) {
        toast.error(e instanceof Error ? e.message : "Could not activate the Free plan");
      }
      return;
    }
    if (!plan.stripe_price_id) {
      toast.error("This plan isn't connected to payments yet.");
      return;
    }
    if (!isPaymentsConfigured()) {
      toast.error("Payments aren't configured in this environment.");
      return;
    }

    const hasActiveSub =
      !!ent.subscription?.stripe_subscription_id &&
      ["active", "trialing", "past_due"].includes(ent.subscription.status);

    if (hasActiveSub) {
      setChanging(true);
      try {
        await changePlan({
          data: {
            organizationId: currentOrg.id,
            newPriceId: plan.stripe_price_id,
            environment: getStripeEnvironment(),
          },
        });
        toast.success(`Switched to ${plan.name}. Proration applied on next invoice.`);
        await ent.refresh();
        goToReturnTo();
      } catch (e) {
        toast.error(e instanceof Error ? e.message : "Failed to change plan");
      } finally {
        setChanging(false);
      }
      return;
    }

    // Pipe `return_to` through Stripe so the post-checkout page can send the
    // user back to where they were trying to do something (e.g. /sites/new).
    // Belt-and-suspenders: also stash in sessionStorage in case the query
    // param is stripped anywhere along the redirect chain, and mirror it
    // into the Stripe session metadata (server-side) as a durable record.
    stashCheckoutReturnTo(search.returnTo);
    const ret = search.returnTo ? `&return_to=${encodeURIComponent(search.returnTo)}` : "";
    openCheckout({
      priceId: plan.stripe_price_id,
      organizationId: currentOrg.id,
      organizationName: currentOrg.name,
      customerEmail: user?.email ?? undefined,
      returnTo: search.returnTo,
      returnUrl: `${window.location.origin}/checkout/return?session_id={CHECKOUT_SESSION_ID}${ret}`,
    });
  };

  // Deep-link auto-trigger: when /billing?plan=<key> is opened, find the
  // matching plan card for the chosen interval and kick off the upgrade flow
  // once plans have loaded and we have an org context.
  useEffect(() => {
    if (autoTriggeredRef.current) return;
    if (!search.plan || loading || !currentOrg || plans.length === 0 || !canManage) return;
    const candidates = plans.filter((p) => p.key === search.plan);
    if (candidates.length === 0) return;
    const match =
      candidates.find((p) => p.interval === billingInterval) ?? candidates[0];

    trackAppEvent(
      "billing_deep_link_matched",
      {
        plan_key: match.key,
        interval: match.interval,
        return_to: search.returnTo,
      },
      { userId: user?.id, orgId: currentOrg?.id },
    );

    autoTriggeredRef.current = true;
    setBillingInterval(match.interval as "month" | "year");
    setHighlightedPlanKey(match.key);
    // Don't auto-trigger if the user is already on this plan.
    if (ent.plan_key === match.key && !ent.exempt) return;

    trackAppEvent(
      "billing_auto_trigger_started",
      { plan_key: match.key, return_to: search.returnTo },
      { userId: user?.id, orgId: currentOrg?.id },
    );

    handleSelectPlan(match, { autoTriggered: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search.plan, loading, plans, currentOrg, canManage, ent.plan_key, ent.exempt]);

  const handleManageBilling = async () => {
    if (!currentOrg) return;
    try {
      const { url } = await openPortal({
        data: {
          organizationId: currentOrg.id,
          returnUrl: `${window.location.origin}/billing`,
          environment: getStripeEnvironment(),
        },
      });
      window.open(url, "_blank", "noreferrer");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Could not open billing portal");
    }
  };

  const handleCancelSubscription = async () => {
    if (!currentOrg) return;
    setCancelling(true);
    try {
      await cancelSub({
        data: { organizationId: currentOrg.id, environment: getStripeEnvironment() },
      });
      toast.success("Subscription will end at the close of the current billing period.");
      await ent.refresh();
      setCancelOpen(false);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Could not cancel subscription");
    } finally {
      setCancelling(false);
    }
  };

  const handleReactivate = async () => {
    if (!currentOrg) return;
    setReactivating(true);
    try {
      await reactivate({
        data: { organizationId: currentOrg.id, environment: getStripeEnvironment() },
      });
      toast.success("Subscription reactivated — it will continue renewing.");
      await ent.refresh();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Could not reactivate subscription");
    } finally {
      setReactivating(false);
    }
  };

  const handleSyncTaxCodes = async () => {
    setSyncingTax(true);
    try {
      const res = await runTaxSync({ data: { environment: getStripeEnvironment() } });
      const okCount = res.updated.length;
      const failCount = res.failed.length;
      if (failCount === 0) {
        toast.success(`Tax codes synced on ${okCount} product(s).`);
      } else {
        toast.warning(`Synced ${okCount}, ${failCount} failed. Check the server logs.`);
      }
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Tax code sync failed");
    } finally {
      setSyncingTax(false);
    }
  };

  if (!currentOrg && !ent.exempt) {
    return (
      <div className="container max-w-3xl py-8">
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            Select an organization to view billing.
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <>
    <PaymentTestModeBanner />
    <div className="container max-w-6xl py-6 space-y-6">
      <Dialog open={checkoutOpen} onOpenChange={(o) => { if (!o) closeCheckout(); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto p-0">
          <DialogHeader className="px-6 pt-6">
            <DialogTitle>Start your 30-day free trial</DialogTitle>
          </DialogHeader>
          <div className="p-4">{checkoutElement}</div>
        </DialogContent>
      </Dialog>
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Billing & plan</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Manage your MissionReady subscription, payment method, and invoice history.
          </p>
        </div>
        <Link to="/settings">
          <Button variant="outline" size="sm">Back to settings</Button>
        </Link>
      </header>

      {ent.exempt && (
        <Card className="border-primary/40 bg-primary/5">
          <CardHeader className="flex flex-row items-center gap-3 space-y-0">
            <ShieldCheck className="h-5 w-5 text-primary" />
            <div className="flex-1">
              <CardTitle className="text-base">Platform owner — billing exempt</CardTitle>
              <CardDescription>
                Your account is permanently exempt from billing checks across all organizations.
              </CardDescription>
            </div>
          </CardHeader>
          <CardContent className="flex flex-wrap items-center gap-3">
            <Button size="sm" variant="outline" onClick={handleSyncTaxCodes} disabled={syncingTax}>
              <RefreshCw className="h-4 w-4" /> {syncingTax ? "Syncing…" : `Sync tax codes (${getStripeEnvironment()})`}
            </Button>
            <span className="text-xs text-muted-foreground">
              Backfills <code className="font-mono">tax_code</code> on every Stripe product so automatic tax can calculate at checkout.
            </span>
          </CardContent>
        </Card>
      )}

      {(() => {
        const paymentsEnabled = plans.some((p) => !!p.stripe_price_id);
        const customerId = ent.subscription?.stripe_customer_id ?? null;
        const latest = invoices[0] ?? null;
        return (
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between gap-3">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <CreditCard className="h-4 w-4" /> Billing status
                  </CardTitle>
                  <CardDescription>Live status of payments for this organization.</CardDescription>
                </div>
                <Badge
                  variant="outline"
                  className={
                    paymentsEnabled
                      ? "bg-success/15 text-success border-success/40"
                      : "bg-amber-500/15 text-amber-400 border-amber-500/40"
                  }
                >
                  {paymentsEnabled ? "Payments enabled" : "Payments not enabled"}
                </Badge>
              </div>
            </CardHeader>
            <CardContent className="grid gap-4 sm:grid-cols-3 text-sm">
              <div>
                <div className="text-muted-foreground uppercase text-xs tracking-wider">Stripe customer</div>
                <div className="font-mono text-xs mt-1 break-all">
                  {customerId ?? <span className="text-muted-foreground font-sans">Not yet created</span>}
                </div>
              </div>
              <div>
                <div className="text-muted-foreground uppercase text-xs tracking-wider">Latest invoice</div>
                <div className="mt-1">
                  {latest ? (
                    <div className="flex flex-wrap items-center gap-2">
                      <StatusBadge status={latest.status} />
                      <span className="text-xs text-muted-foreground">
                        {fmtMoney(latest.amount_paid_cents || latest.amount_due_cents, latest.currency)} · {fmtDate(latest.created_at)}
                      </span>
                    </div>
                  ) : (
                    <span className="text-muted-foreground">No invoices yet</span>
                  )}
                </div>
              </div>
              <div>
                <div className="text-muted-foreground uppercase text-xs tracking-wider">Last payment</div>
                <div className="mt-1">
                  {latest?.paid_at
                    ? fmtDate(latest.paid_at)
                    : <span className="text-muted-foreground">{latest ? "Unpaid" : "—"}</span>}
                </div>
              </div>
            </CardContent>
          </Card>
        );
      })()}

      {(() => {
        if (ent.exempt) return null;
        const failed = invoices.find((i) => FAILURE_STATUSES.has(i.status) || (i.failure_code || i.failure_reason));
        if (!failed) return null;
        const handleRetry = async () => {
          if (failed.hosted_invoice_url) {
            window.open(failed.hosted_invoice_url, "_blank", "noreferrer");
            return;
          }
          toast.info("Retry will trigger a new payment attempt once Stripe is connected.");
        };
        return (
          <Card className="border-destructive/50 bg-destructive/5">
            <CardHeader>
              <div className="flex items-start gap-3">
                <XCircle className="h-5 w-5 text-destructive mt-0.5 shrink-0" />
                <div className="flex-1">
                  <CardTitle className="text-base text-destructive">Payment failed</CardTitle>
                  <CardDescription className="text-foreground/80 mt-1">
                    {friendlyFailureReason(failed.failure_code, failed.failure_reason)}
                  </CardDescription>
                </div>
                <StatusBadge status={failed.status} />
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-3 sm:grid-cols-3 text-sm">
                <div>
                  <div className="text-muted-foreground uppercase text-xs tracking-wider">Amount due</div>
                  <div className="font-medium mt-1">{fmtMoney(failed.amount_due_cents - failed.amount_paid_cents, failed.currency)}</div>
                </div>
                <div>
                  <div className="text-muted-foreground uppercase text-xs tracking-wider">Attempts</div>
                  <div className="font-medium mt-1">{failed.attempt_count || 1}</div>
                </div>
                <div>
                  <div className="text-muted-foreground uppercase text-xs tracking-wider">Next auto-retry</div>
                  <div className="font-medium mt-1">
                    {failed.next_payment_attempt
                      ? fmtDateTime(failed.next_payment_attempt)
                      : <span className="text-muted-foreground">No retry scheduled</span>}
                  </div>
                </div>
              </div>
              {failed.failure_code && (
                <div className="text-xs text-muted-foreground">
                  Reason code: <span className="font-mono">{failed.failure_code}</span>
                  {failed.number && <> · Invoice <span className="font-mono">{failed.number}</span></>}
                </div>
              )}
              {canManage && (
                <div className="flex flex-wrap gap-2 pt-1">
                  <Button size="sm" onClick={handleRetry}>
                    <RefreshCw className="h-4 w-4" /> Retry payment
                  </Button>
                  <Button size="sm" variant="outline" onClick={handleManageBilling}>
                    <CreditCard className="h-4 w-4" /> Update payment method
                  </Button>
                  {failed.hosted_invoice_url && (
                    <a href={failed.hosted_invoice_url} target="_blank" rel="noreferrer">
                      <Button size="sm" variant="ghost">
                        <ExternalLink className="h-4 w-4" /> View invoice
                      </Button>
                    </a>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        );
      })()}

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between gap-3">
            <div>
              <CardTitle>Current subscription</CardTitle>
              <CardDescription>{currentOrg?.name ?? "Platform"}</CardDescription>
            </div>
            <StatusBadge status={ent.exempt ? "active" : ent.status} />
          </div>
        </CardHeader>
        <CardContent className="grid gap-4 sm:grid-cols-3 text-sm">
          <div>
            <div className="text-muted-foreground uppercase text-xs tracking-wider">Plan</div>
            <div className="font-medium mt-1 flex items-center gap-2 flex-wrap">
              <span>
                {ent.exempt
                  ? "Platform"
                  : (() => {
                      const matched = plans.find((p) => p.key === ent.plan_key);
                      if (matched) return matched.name;
                      if (ent.plan_key) return ent.plan_key.charAt(0).toUpperCase() + ent.plan_key.slice(1);
                      return "No plan";
                    })()}
              </span>
              <StatusBadge status={ent.exempt ? "active" : ent.status} />
            </div>
            {!ent.exempt && ent.plan_key && (() => {
              const matched = plans.find((p) => p.key === ent.plan_key);
              return matched ? (
                <div className="text-xs text-muted-foreground mt-1">
                  {fmtMoney(matched.price_cents, matched.currency)}/{matched.interval}
                </div>
              ) : null;
            })()}
          </div>
          <div>
            <div className="text-muted-foreground uppercase text-xs tracking-wider">
              {ent.status === "trialing" ? "Trial ends" : "Renews"}
            </div>
            <div className="font-medium mt-1">
              {fmtDate(ent.subscription?.trial_ends_at ?? ent.subscription?.current_period_end ?? null)}
            </div>
            {ent.status === "trialing" && (
              <div className="text-xs text-muted-foreground mt-1">
                No charges until trial ends.
              </div>
            )}
          </div>
          <div>
            <div className="text-muted-foreground uppercase text-xs tracking-wider">Cancels at period end</div>
            <div className="font-medium mt-1">{ent.subscription?.cancel_at_period_end ? "Yes" : "No"}</div>
          </div>
          {canManage && !ent.exempt && (
            <div className="sm:col-span-3 flex flex-wrap gap-2 pt-2">
              <Button variant="outline" size="sm" onClick={handleManageBilling}>
                <CreditCard className="h-4 w-4" /> Manage payment method
              </Button>
              <Button variant="outline" size="sm" onClick={handleManageBilling}>
                Update billing details
              </Button>
              {ent.subscription?.stripe_subscription_id &&
                ent.subscription.cancel_at_period_end &&
                ["active", "trialing", "past_due"].includes(ent.status) && (
                  <Button
                    variant="default"
                    size="sm"
                    onClick={handleReactivate}
                    disabled={reactivating}
                  >
                    <RefreshCw className="h-4 w-4" /> {reactivating ? "Reactivating…" : "Reactivate subscription"}
                  </Button>
                )}
              {ent.subscription?.stripe_subscription_id &&
                !ent.subscription.cancel_at_period_end &&
                ["active", "trialing", "past_due"].includes(ent.status) && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-destructive hover:text-destructive"
                    onClick={() => setCancelOpen(true)}
                  >
                    <XCircle className="h-4 w-4" /> Cancel subscription
                  </Button>
                )}
            </div>
          )}
        </CardContent>
      </Card>

      <Tabs defaultValue="plans">
        <TabsList>
          <TabsTrigger value="plans">Plans</TabsTrigger>
          <TabsTrigger value="invoices">Invoices</TabsTrigger>
        </TabsList>

        <TabsContent value="plans" className="mt-4 space-y-4">
          {(search.plan || search.returnTo) && (
            <Card className="border-primary/40 bg-primary/5">
              <CardContent className="flex flex-wrap items-center justify-between gap-3 pt-6">
                <div className="text-sm">
                  {search.plan && highlightedPlanKey ? (
                    <>
                      We've preselected <span className="font-semibold">
                        {plans.find((p) => p.key === highlightedPlanKey)?.name.replace(/\s*\(Annual\)\s*$/i, "")
                          ?? highlightedPlanKey}
                      </span> for you.
                    </>
                  ) : (
                    <>Pick a plan to continue.</>
                  )}
                  {search.returnTo && (
                    <> You'll be sent back to <span className="font-mono text-xs">{search.returnTo}</span> when you're done.</>
                  )}
                </div>
                {search.returnTo && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => navigate({ to: search.returnTo as any })}
                  >
                    Cancel and go back
                  </Button>
                )}
              </CardContent>
            </Card>
          )}


          <Card>
            <CardContent className="flex flex-wrap items-center justify-between gap-3 pt-6">
              <div>
                <div className="text-sm font-medium">Billing interval</div>
                <p className="text-xs text-muted-foreground mt-0.5">
                  All paid plans include a <span className="text-foreground font-medium">30-day free trial</span>. No card charged until trial ends.
                </p>
              </div>
              <div className="inline-flex rounded-md border overflow-hidden">
                <button
                  type="button"
                  onClick={() => setBillingInterval("month")}
                  className={`px-3 py-1.5 text-xs font-medium transition-colors ${billingInterval === "month" ? "bg-primary text-primary-foreground" : "bg-background text-muted-foreground hover:text-foreground"}`}
                >
                  Monthly
                </button>
                <button
                  type="button"
                  onClick={() => setBillingInterval("year")}
                  className={`px-3 py-1.5 text-xs font-medium transition-colors flex items-center gap-1.5 ${billingInterval === "year" ? "bg-primary text-primary-foreground" : "bg-background text-muted-foreground hover:text-foreground"}`}
                >
                  Annual
                  <span className={`text-[10px] px-1.5 py-0.5 rounded ${billingInterval === "year" ? "bg-primary-foreground/20" : "bg-success/15 text-success"}`}>
                    2 months free
                  </span>
                </button>
              </div>
            </CardContent>
          </Card>

          {loading ? (
            <div className="text-sm text-muted-foreground py-8 text-center">Loading plans…</div>
          ) : (
            <div className="grid gap-4 md:grid-cols-3">
              {plans.filter((p) => p.interval === billingInterval).map((p) => {
                const current = ent.plan_key === p.key && !ent.exempt;
                const isAnnual = p.interval === "year";
                const monthlyEquivalent = isAnnual ? Math.round(p.price_cents / 12) : null;
                return (
                  <Card
                    key={p.id}
                    className={
                      current
                        ? "border-primary/60 ring-1 ring-primary/30"
                        : highlightedPlanKey === p.key
                          ? "border-primary ring-2 ring-primary/40 shadow-lg"
                          : ""
                    }
                  >
                    <CardHeader>
                      <div className="flex items-center justify-between">
                        <CardTitle>{p.name.replace(/\s*\(Annual\)\s*$/i, "")}</CardTitle>
                        {current ? <Badge>Current</Badge> : highlightedPlanKey === p.key ? <Badge variant="outline" className="border-primary/60 text-primary">Recommended</Badge> : null}
                      </div>
                      <CardDescription>{p.description}</CardDescription>
                      <div className="pt-2">
                        <span className="text-3xl font-bold">{fmtMoney(p.price_cents, p.currency)}</span>
                        <span className="text-sm text-muted-foreground">/{p.interval === "year" ? "yr" : "mo"}</span>
                        {isAnnual && monthlyEquivalent != null && (
                          <div className="mt-1 text-xs text-muted-foreground">
                            ≈ {fmtMoney(monthlyEquivalent, p.currency)}/mo · billed annually · 2 months free
                          </div>
                        )}
                        {p.key !== "free" && (
                          <div className="mt-1 text-xs text-success font-medium">
                            Includes 30-day free trial
                          </div>
                        )}
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <ul className="space-y-2 text-sm">
                        {p.features.map((f, i) => (
                          <li key={i} className="flex items-start gap-2">
                            <Check className="h-4 w-4 text-primary shrink-0 mt-0.5" />
                            <span>{f}</span>
                          </li>
                        ))}
                      </ul>
                      <Button
                        className="w-full"
                        variant={current ? "outline" : "default"}
                        disabled={current || !canManage}
                        onClick={() => handleSelectPlan(p)}
                      >
                        {current ? "Current plan" : p.key === "free" ? "Switch to Free" : "Start 30-day free trial"}
                      </Button>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>

        <TabsContent value="invoices" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-4 w-4" /> Invoice history
              </CardTitle>
              <CardDescription>
                Showing the last 50 invoices for this organization.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {invoices.length === 0 ? (
                <div className="text-sm text-muted-foreground py-8 text-center">
                  No invoices yet. Once your subscription is active, charges will appear here.
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Number</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead>Amount</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {invoices.map((inv) => (
                      <TableRow key={inv.id}>
                        <TableCell className="font-mono text-xs">{inv.number ?? inv.id.slice(0, 8)}</TableCell>
                        <TableCell>{fmtDate(inv.created_at)}</TableCell>
                        <TableCell>{fmtMoney(inv.amount_paid_cents || inv.amount_due_cents, inv.currency)}</TableCell>
                        <TableCell><StatusBadge status={inv.status} /></TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-2">
                            {inv.hosted_invoice_url && (
                              <a href={inv.hosted_invoice_url} target="_blank" rel="noreferrer">
                                <Button variant="outline" size="sm"><ExternalLink className="h-3 w-3" /> View</Button>
                              </a>
                            )}
                            {inv.invoice_pdf && (
                              <a href={inv.invoice_pdf} target="_blank" rel="noreferrer">
                                <Button variant="outline" size="sm">PDF</Button>
                              </a>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

    </div>

    <AlertDialog open={cancelOpen} onOpenChange={setCancelOpen}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Cancel subscription?</AlertDialogTitle>
          <AlertDialogDescription>
            You'll keep access to all paid features until{" "}
            <span className="font-medium text-foreground">
              {ent.subscription?.current_period_end
                ? new Date(ent.subscription.current_period_end).toLocaleDateString()
                : "the end of the current billing period"}
            </span>
            . After that your organization will be downgraded. You can resubscribe any time.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={cancelling}>Keep subscription</AlertDialogCancel>
          <AlertDialogAction
            disabled={cancelling}
            onClick={(e) => {
              e.preventDefault();
              handleCancelSubscription();
            }}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
          >
            {cancelling ? "Cancelling…" : "Cancel subscription"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
    </>
  );
}
